import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';
import { useScene } from '../scene-context';

export interface PortalsProps {
  children: React.ReactNode;
  className?: string;
  doorImage?: string;
}

export function Portals({ children, className, doorImage }: PortalsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { pack } = useScene();
  const shouldReduceMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'center center'],
  });

  // Uniform door opening animation: doors split symmetrically outward from center
  const doorLeftX = useTransform(scrollYProgress, [0.1, 0.8], ['0%', '-100%']);
  const doorRightX = useTransform(scrollYProgress, [0.1, 0.8], ['0%', '100%']);
  const opacity = useTransform(scrollYProgress, [0.6, 0.95], [1, 0]);

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full h-full overflow-hidden isolate z-0', className)}
    >
      {/* Uniform Archway Door Splitting Portal Overlay */}
      {!shouldReduceMotion && (
        <motion.div style={{ opacity }} className="absolute inset-0 pointer-events-none z-20 flex">
          {/* Left Door Half */}
          <motion.div
            className="w-1/2 h-full border-r border-amber-300/30"
            style={{
              x: doorLeftX,
              background: doorImage
                ? `url(${doorImage}) left center / cover no-repeat`
                : `linear-gradient(90deg, ${pack.colors.background}, ${pack.colors.secondary}ee)`,
            }}
          />
          {/* Right Door Half */}
          <motion.div
            className="w-1/2 h-full border-l border-amber-300/30"
            style={{
              x: doorRightX,
              background: doorImage
                ? `url(${doorImage}) right center / cover no-repeat`
                : `linear-gradient(270deg, ${pack.colors.background}, ${pack.colors.secondary}ee)`,
            }}
          />
        </motion.div>
      )}

      {/* Internal Content Revealed by Door Opening */}
      <div className="relative z-10 w-full h-full flex items-center justify-center">
        {children}
      </div>
    </div>
  );
}
